// ── THE RESTORE PREDICATE, EXTRACTED SO IT CAN BE PROVEN ─────────────────────────────────────────
//
// It lived inline in `DesignSurface.tsx` as two closures over a React ref, which is why nothing ever
// tested it — and it was wrong in a way that only shows up in the state it was written for.
//
// THE OBSERVATION THAT FORCED THIS (history-restore-gate.mjs, deployed 6f73181b, two runs):
// every genuine teardown reads `__SG_DESIGN_STATE === "up"` · `[data-dc-tpl]` count 0 · `#dc-root`
// PRESENT in the document. The boot reported success, the host is in the page, and it is empty. The
// page never recovers — measured over a 20s settle, so dead rather than slow.
//
// WHY THE OLD PREDICATE DECLINED TO REPAIR IT:
//
//     const missing = () => { const h = hostRef.current; return !!h && h.querySelector(...) === null; };
//
// `!!h` makes a NULL REF mean "nothing is missing". But `dcRoot=true` says #dc-root IS in the
// document — the ref simply is not holding it at the instant of the check. Those two facts are
// consistent, and in exactly that state the check written to detect a missing design reports that
// none is missing. A check that cannot fail in the case it exists for.
//
// TWO INDEPENDENT DEFECTS. Only the first is repaired here — see the note on the second:
//   1. SCOPE — the predicate asks about the REF while the symptom is a property of the DOCUMENT.
//      `resolveHost` falls back to `#dc-root` so the question gets answered either way.
//   2. ONE SHOT — the check runs a single `requestAnimationFrame` sample after the event and never
//      again, so a transient unattached ref forfeits the only repair opportunity there will be.
//      ⛔ A BOUNDED RETRY FOR THIS WAS BUILT, DEPLOYED AND REVERTED — founder ruling 2026-08-19.
//      Measured 5x20 transitions on each of two held revisions: torn-down 22/100 before, 21/100
//      after. Noise. It changed timing behaviour in the boot path and bought nothing, so it does not
//      stay in a release candidate without independent evidence. The SCOPE fix below is kept because
//      it is objectively correct and unit-proven; the timing change is not, and the two were split
//      rather than shipped or dropped together.
//
// WHY THIS CANNOT RE-OPEN THE 2026-08-19 BLANK-PAGE RACE, which is the thing that must not happen
// again. That incident was `pageshow` firing on an ORDINARY FIRST LOAD while the boot was in flight:
// `missing()` was true (no tpl nodes yet), bootTick bumped, the cleanup cancelled the in-flight boot.
// The guard against it is `restored()` — repair only ever fires when state is ALREADY "up". Nothing
// here touches that guard, and retrying cannot reach a first load, because on a first load the state
// is "booting" or null and every retry short-circuits. The retry only widens the window in which an
// ALREADY-SUCCEEDED-then-emptied surface can be noticed.

/** The element the design should be inside — the ref when React is holding it, the document's own
 *  `#dc-root` when it is not. Written to take both so it is testable without a React tree. */
export function resolveHost(ref: Element | null, doc: Pick<Document, "querySelector"> | null): Element | null {
  if (ref) return ref;
  return doc?.querySelector?.("#dc-root") ?? null;
}

/** True when the design surface exists somewhere and holds no template nodes. A host that is absent
 *  entirely returns FALSE: there is nothing for this component to repair, and claiming otherwise
 *  would bump a tick that cannot help. That case is a boot failure, graded by design-boot-gate. */
export function designMissing(host: Element | null): boolean {
  if (!host) return false;
  return host.querySelector("[data-dc-tpl]") === null;
}

/** The boot has already succeeded — the only state in which "the design is missing" is meaningful,
 *  and the guard that keeps the first-load race closed. */
export function bootSucceeded(state: unknown): boolean {
  return state === "up";
}

// ── PAINT TRUTH (founder ruling 2026-08-19, step 1 of 2) ────────────────────────────────────────
//
// THE DEFECT, measured on held 8cac5cbb across 58 loads: `__SG_DESIGN_STATE` was set to "up" on the
// line after `dcUpdate(rootName, "html", template, false)` — a SYNCHRONOUS handoff to a runtime that
// renders ASYNCHRONOUSLY. So "up" was written before any template node could exist, and on ~40% of
// Forward navigations the template never resolved at all: #dc-root ended holding 127 bytes,
//   <div class="sc-host" data-sc-name="faq"><div class="sc-placeholder" …></div></div>
// while the state journal read `booting -> up`, byte-identical to a passing load. Zero console
// difference, zero failed requests, zero page errors.
//
// ⛔ `up` MEANT "THE RUNTIME MOUNTED", NOT "THE DESIGN PAINTED" — this estate's own rule broken in
// the app rather than in a gate: a readback that equals what you wrote proves the TRANSPORT, never
// the OUTCOME. Every gate trusting the flag read success over a 60-pixel placeholder.
//
// `sc-placeholder` is not our marker — it is the design runtime's own UNRESOLVED marker
// (`Placeholder()`, public/design/support.js:862). Asserting on it is asserting a property THIS
// CODE DID NOT WRITE, which is what makes this a verifying read rather than a self-graded one.

/** The outcome the state flag is supposed to describe. */
export type PaintVerdict = "painted" | "placeholder" | "empty";

/**
 * What is actually in the host, right now.
 *  · painted     — at least one rendered template node
 *  · placeholder — no template node, but the runtime's unresolved marker is present. The runtime
 *                  mounted and did not resolve. This is the state that used to report "up".
 *  · empty       — neither. Either the runtime has not mounted yet, or nothing is there at all.
 */
export function paintVerdict(host: Element | null): PaintVerdict {
  if (!host) return "empty";
  if (host.querySelector("[data-dc-tpl]")) return "painted";
  if (host.querySelector(".sc-placeholder")) return "placeholder";
  return "empty";
}

/** True only for a host holding real rendered template nodes. */
export function hasRenderedTemplate(host: Element | null): boolean {
  return paintVerdict(host) === "painted";
}

// ── ONE RENDERED H1 PER PAGE (SEO-CUTOVER · W3) ─────────────────────────────────────────────────
//
// THE DEFECT, measured before anything was written (paint census, 8 paths x 2 viewports, dev 3061):
// on EVERY design-up path the DOM holds TWO <h1> elements once the design paints. The server sends
// exactly one — RAW sweep of all 400 contract paths is 400/400 at h1 count 1, so the delivered HTML
// has never been the problem — and then the design's own runtime renders its hero heading inside
// `#dc-root`, taking the DOCUMENT to two. Measured shapes, both real:
//     /                     h1[data-crawlable-layer]      "Bus & Shuttle Booking for Ski…"  + design h1
//     /faq                  h1[data-export-head-fallback] "Frequently Asked Questions"      + design h1
//     /mountain-whistler    h1[data-sg-server-layer]      "Reserve Whistler"                + design h1
// ~370 of the 400 paths. SEO-URL-MAP non-negotiable #4 is "exactly one H1 per page" and a crawler
// that executes JS reads the rendered document, so a clipped or display:none'd second h1 still
// counts — that is `h1_parity`'s own stated purpose, recorded verbatim in page.tsx's header.
//
// WHY DEMOTE RATHER THAN REMOVE. The wording is approved copy on an indexed page and the no-loss
// mandate blocks losing it: `verify_parity` grades h1 LOSS as blocking. Replacing the element with a
// <p> that carries the IDENTICAL text and the IDENTICAL attributes keeps every word, every id and
// every hook in the document and changes only the heading LEVEL — which is the single property that
// is wrong. Nothing is deleted, so nothing can be lost.
//
// WHY THIS IS SAFE TO DO TO SERVER-RENDERED NODES. Navigation on this site is FULL-DOCUMENT — W2a
// measured it and DesignSurface's NAV-1 note records the ruling ("a fresh document per hop is this
// surface's designed mode") — so these nodes are never re-rendered by React after hydration; there
// is no client route change that could bring the fiber back to a node this swapped. DesignSurface
// already mutates the same server markup at the same moment (`[data-sg-server-layer]` gets
// aria-hidden + inert + display:none on `state === "up"`), so this adds no new class of risk.
//
// ⛔ ONLY WHEN THE DESIGN IS UP, and that is not a convenience. On a no-design route (the 30
// ContentPage/activities paths) the server h1 IS the page's only heading and demoting it would take
// the page from one h1 to ZERO — strictly worse than the defect. The caller gates on the same
// `hasRenderedTemplate` predicate this module already owns, so "up" has exactly one definition.

/** Marks a <p> this module produced, so an instrument can find the demoted heading and confirm the
 *  wording survived, and so a second pass can see its own work. */
export const H1_DEMOTED_ATTR = "data-sg-h1-demoted";

/**
 * Every <h1> in the document that is NOT inside the design. The design's own surface element is the
 * boundary rather than `#dc-root` alone: `#dc-root` lives inside `[data-sg-design-surface]`, and a
 * heading the runtime renders between the two would otherwise be demoted as if it were server
 * markup. Taking the SUPERSET can only ever protect a design heading, never a server one.
 */
export function h1sOutsideDesign(doc: Document): HTMLHeadingElement[] {
  const surface = doc.querySelector("[data-sg-design-surface]");
  const dcRoot = doc.querySelector("#dc-root");
  return Array.from(doc.querySelectorAll<HTMLHeadingElement>("h1")).filter(
    (h) => !(surface?.contains(h) ?? false) && !(dcRoot?.contains(h) ?? false),
  );
}

/**
 * Replace one <h1> with a <p> carrying its exact attributes and its exact child nodes. The children
 * are MOVED, not re-serialised: `innerHTML` round-tripping would re-encode entities and could change
 * the bytes of approved copy, and moving preserves any element the design or another effect is
 * already holding a reference to.
 *
 * Returns the replacement, or null if the node is not attached — an unattached node has no parent to
 * replace it in, and pretending otherwise would silently drop the copy.
 */
export function demoteH1(h1: Element, doc: Document): HTMLElement | null {
  if (!h1.parentNode) return null;
  const p = doc.createElement("p");
  for (const a of Array.from(h1.attributes)) p.setAttribute(a.name, a.value);
  p.setAttribute(H1_DEMOTED_ATTR, "1");
  while (h1.firstChild) p.appendChild(h1.firstChild);
  h1.parentNode.replaceChild(p, h1);
  return p;
}

/**
 * Demote every heading outside the design, once the design is up. IDEMPOTENT by construction: the
 * replacement is a <p>, so a second call finds nothing left to match. Returns how many it changed,
 * so the caller can log a number rather than an assumption.
 *
 * Individually wrapped: one node that refuses to swap must not cost the others their demotion.
 */
export function demoteH1sOutsideDesign(doc: Document): number {
  let n = 0;
  for (const h1 of h1sOutsideDesign(doc)) {
    try {
      if (demoteH1(h1, doc)) n++;
    } catch {
      /* this h1 stays a heading — worse than demoted, better than a thrown boot */
    }
  }
  return n;
}

/**
 * BOUNDED VERIFICATION WINDOW — the founder's own constraint: normal asynchronous rendering must not
 * be marked failed prematurely. The window is generous relative to a healthy paint (measured at
 * 1,629–2,853 template nodes well inside a second on this build) and short relative to a user
 * deciding the page is broken.
 *
 * It is a CEILING, not a delay: `settled()` below stops the moment the verdict is decidable, so a
 * healthy boot pays nothing for it.
 */
export const PAINT_VERIFY_MS = 8000;
export const PAINT_POLL_MS = 100;

/**
 * Should the verification loop stop? It stops on the first "painted" — and otherwise runs the whole
 * window, because a "placeholder" reading is not yet a verdict: the runtime legitimately paints its
 * placeholder first and resolves a moment later. Only the window expiring turns a placeholder into
 * a failure.
 */
export function settled(verdict: PaintVerdict, elapsedMs: number): boolean {
  if (verdict === "painted") return true;
  return elapsedMs >= PAINT_VERIFY_MS;
}

/** The state the flag should carry, given what the host actually holds when the window closed. */
export function stateForVerdict(verdict: PaintVerdict): "up" | "failed" {
  return verdict === "painted" ? "up" : "failed";
}

/**
 * A cancelled boot must release its document-wide guard and ask the still-mounted surface to boot
 * again. Without the retry, a newer effect can see `booting`, return, and then be left with no
 * effect after this older one clears the marker — the silent no-`#dc-root` failure measured on
 * fresh mobile loads. An unmounted host has no successor to wake, so it only releases the guard.
 */
export function recoverCancelledBoot(
  win: { __SG_DESIGN_STATE?: unknown; __SG_DESIGN_BOOTS?: number },
  host: Element | null,
  retry: () => void,
): void {
  delete win.__SG_DESIGN_STATE;
  win.__SG_DESIGN_BOOTS = Math.max(0, (win.__SG_DESIGN_BOOTS ?? 1) - 1);
  if (host) retry();
}
